import {z} from 'zod';
import {defineToolRegistryManifest} from '../../types/base-tool-types';

const toolName = 'list-sites' as const;

/** Default page size when the caller doesn't specify a limit. */
export const LIST_SITES_DEFAULT_LIMIT = 25;
/** Hard cap on how many sites a single call can return. */
export const LIST_SITES_MAX_LIMIT = 100;

const argumentZod = z.object({
  search: z
    .string()
    .optional()
    .describe(
      'Case-insensitive substring filter on a site’s display name or short ' +
        'name. Use this to narrow large workspaces when the user named a site.'
    ),
  limit: z
    .number()
    .int()
    .min(1)
    .max(LIST_SITES_MAX_LIMIT)
    .optional()
    .describe(
      `Maximum number of sites to return (default ${LIST_SITES_DEFAULT_LIMIT}, max ${LIST_SITES_MAX_LIMIT}).`
    ),
  offset: z
    .number()
    .int()
    .min(0)
    .optional()
    .describe(
      'Number of sites to skip, for pagination. Use together with `hasMore` ' +
        'from a previous response to fetch the next page.'
    ),
});

export const listSitesManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'List sites',
  // Deliberately NOT family 'site': the 'site' family is compounded into a
  // single `compound-site` tool in compound/render modes, which would hide
  // `list-sites` behind a `{action: 'list-sites'}` indirection. The site
  // resolution flow (and the Slack prompt) call `list-sites` directly, so it
  // must stay a literal, always-advertised tool. 'site-resolution' is not in
  // COMPOUND_FAMILY_SUMMARIES, so it is never compounded.
  family: 'site-resolution',
  llmTool: {
    name: toolName,
    description:
      'Lists the sites the caller can access, scoped to the current workspace. ' +
      'Use this to resolve which site the user means when no site has been ' +
      'specified or the reference is ambiguous: if exactly one site is returned, ' +
      'proceed with it; if more than one, ask the user which site they mean by ' +
      'name; if none, tell the user they have no accessible sites. ' +
      'Each entry includes the site id, display name, and short name.',
    inputSchema: argumentZod,
  },
  type: 'server',
  permission: [{resource: 'SiteMeta', action: 'read'}],
  confirmBeforeExecuting: false,
  isConcurrencySafe: () => true,
  isReadOnly: () => true,
});

export type ListSitesArgumentType = z.infer<typeof argumentZod>;
